import { maxBy } from 'lodash';
import { RefObject, useCallback, useEffect, useMemo, useState } from 'react';

import { ESeverity, getAvailableHeight } from '#shared';

import { AVAILABILITY_CARD_MIN_HEIGHT } from './constants';

const ROW_GAP = 8;
const ROW_MIN = AVAILABILITY_CARD_MIN_HEIGHT;
const SEVS: ESeverity[] = [
  ESeverity.NA,
  ESeverity.CRITICAL,
  ESeverity.MAJOR,
  ESeverity.LOW,
];

type SeverityWithoutMinor = Exclude<ESeverity, ESeverity.MINOR>;
type Groups = Record<SeverityWithoutMinor, Array<unknown>>;
type RowsBy = Record<SeverityWithoutMinor, number>;

const EMPTY_ROWS: RowsBy = {
  [ESeverity.NA]: 0,
  [ESeverity.CRITICAL]: 0,
  [ESeverity.MAJOR]: 0,
  [ESeverity.LOW]: 0,
};

export const useRowsDistribution = (
  rootRef: RefObject<HTMLElement | null>,
  groups: Groups,
) => {
  const [containerHeight, setContainerHeight] = useState(0);

  const lengths = useMemo(
    () => ({
      [ESeverity.NA]: groups[ESeverity.NA]?.length ?? 0,
      [ESeverity.CRITICAL]: groups[ESeverity.CRITICAL]?.length ?? 0,
      [ESeverity.MAJOR]: groups[ESeverity.MAJOR]?.length ?? 0,
      [ESeverity.LOW]: groups[ESeverity.LOW]?.length ?? 0,
    }),
    [groups],
  );

  // Keep last decided max rows
  const [lastMaxRows, setLastMaxRows] = useState<number | null>(null);

  const compute = useCallback(
    (heightInput: number, lens = lengths): RowsBy => {
      const decideMaxRows = (H: number, visibleCount: number) => {
        const groupGaps = Math.max(0, visibleCount - 1) * ROW_GAP;

        const raw = (H - groupGaps + ROW_GAP) / (ROW_MIN + ROW_GAP);
        const baseline = Math.max(visibleCount, Math.floor(raw)); // basic lower bound

        // Hysteresis: only change when clearly across the boundary
        const prev = lastMaxRows ?? baseline;

        let next = baseline;

        if (raw > prev) {
          // Allow increase only if we're well into the next slot
          if (raw >= prev + 0.5) {
            next = Math.floor(raw);
          } else {
            next = prev;
          }
        } else if (raw < prev) {
          // Allow decrease only if we're well into the previous slot
          if (raw <= prev - 0.5) {
            next = Math.floor(raw);
          } else {
            next = prev;
          }
        } else {
          next = prev;
        }

        // Always at least 1 row per visible group
        next = Math.max(visibleCount, next);
        setLastMaxRows(next);
        return next;
      };

      const visible = SEVS.filter(k => (lens[k] ?? 0) > 0);
      if (!visible.length) {
        return EMPTY_ROWS;
      }

      // Prefer a stable height read
      const H = Math.round(heightInput);

      const maxTotalRows = decideMaxRows(H, visible.length);

      const next: RowsBy = { ...EMPTY_ROWS };
      visible.forEach(k => (next[k] = 1));

      const remaining = maxTotalRows - visible.length;
      if (remaining > 0) {
        const largestKey = maxBy(visible, k => lens[k] ?? 0)!;
        next[largestKey] += remaining;
      }

      return next;
    },
    [lengths, lastMaxRows],
  );

  // Track container height changes
  useEffect(() => {
    const el = rootRef.current;
    if (!el) {
      return;
    }

    let raf = 0;

    // Initial height - defer to avoid synchronous setState in effect
    raf = requestAnimationFrame(() => {
      const H = el.clientHeight || getAvailableHeight(el);
      setContainerHeight(H);
    });
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const H = el.clientHeight || getAvailableHeight(el);
        setContainerHeight(H);
      });
    });

    ro.observe(el);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [rootRef]);

  // Compute rowsBy from containerHeight and lengths
  const rowsBy = useMemo(
    () => (containerHeight > 0 ? compute(containerHeight) : EMPTY_ROWS),
    [containerHeight, compute],
  );

  return rowsBy;
};
